import React from 'react'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../components/collapsible'
import { cn } from '../../lib/utils'

export interface UICollapsibleProps extends React.ComponentProps<typeof Collapsible> {
  variant?: 'default' | 'accordion' | 'menu'
  spacing?: 'tight' | 'normal' | 'loose'
}

export interface UICollapsibleTriggerProps extends React.ComponentProps<typeof CollapsibleTrigger> {
  variant?: 'default' | 'button' | 'arrow' | 'minimal'
  showIndicator?: boolean
}

export interface UICollapsibleContentProps extends React.ComponentProps<typeof CollapsibleContent> {
  variant?: 'default' | 'padded' | 'minimal'
  spacing?: 'tight' | 'normal' | 'loose'
}

/**
 * Enhanced Collapsible component following design system best practices
 * Provides consistent collapsible behavior with enhanced styling options
 */
export const UICollapsible = React.forwardRef<
  React.ComponentRef<typeof Collapsible>,
  UICollapsibleProps
>(({ className, variant = 'default', spacing = 'normal', ...props }, ref) => {
  return (
    <Collapsible
      ref={ref}
      className={cn(
        // Variant styles
        {
          '': variant === 'default',
          'border border-border rounded-lg': variant === 'accordion',
          'group': variant === 'menu',
        },

        // Spacing styles
        {
          'gap-1': spacing === 'tight',
          'gap-2': spacing === 'normal',
          'gap-3': spacing === 'loose',
        },

        className
      )}
      {...props}
    />
  )
})

export const UICollapsibleTrigger = React.forwardRef<
  React.ComponentRef<typeof CollapsibleTrigger>,
  UICollapsibleTriggerProps
>(({ className, variant = 'default', showIndicator = true, ...props }, ref) => {
  return (
    <CollapsibleTrigger
      ref={ref}
      className={cn(
        // Base styles
        'transition-[background-color,opacity] duration-200 focus:outline-none',

        // Variant styles
        {
          'hover:bg-accent/50 focus:bg-accent/50': variant === 'default',
          'bg-transparent border border-border rounded-md px-3 py-2 hover:bg-accent focus:bg-accent': variant === 'button',
          'hover:bg-accent/50 focus:bg-accent/50 [&[data-state=open]>svg]:rotate-180': variant === 'arrow' && showIndicator,
          'hover:opacity-80 focus:opacity-80': variant === 'minimal',
        },

        className
      )}
      {...props}
    />
  )
})

export const UICollapsibleContent = React.forwardRef<
  React.ComponentRef<typeof CollapsibleContent>,
  UICollapsibleContentProps
>(({ className, variant = 'default', spacing = 'normal', ...props }, ref) => {
  return (
    <CollapsibleContent
      ref={ref}
      className={cn(
        // Base styles - height animation uses keyframe animations (animate-collapsible-up/down)
        'transition-[opacity] duration-200 overflow-hidden',
        'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',

        // Variant styles
        {
          '': variant === 'default',
          'px-3 py-2': variant === 'padded',
          'overflow-visible': variant === 'minimal',
        },

        // Spacing styles
        {
          'space-y-1': spacing === 'tight',
          'space-y-2': spacing === 'normal',
          'space-y-3': spacing === 'loose',
        },

        className
      )}
      {...props}
    />
  )
})

UICollapsible.displayName = "UICollapsible"
UICollapsibleTrigger.displayName = "UICollapsibleTrigger"
UICollapsibleContent.displayName = "UICollapsibleContent"